import type { Toast } from "@/hooks/useToast";
import { cn } from "@/utils";
import { AlertTriangle, CheckCircle, Info, X, XCircle } from "lucide-react";
import { useEffect, useReducer } from "react";

interface ToastProps extends React.HTMLAttributes<HTMLDivElement> {
	toast: Toast;
	onDismiss: (id: string) => void;
	showCloseButton?: boolean;
}

interface AnimationState {
	isVisible: boolean;
	isLeaving: boolean;
}
interface EnterAction {
	type: "ENTER";
}
interface LeaveAction {
	type: "LEAVE";
}
type AnimationAction = EnterAction | LeaveAction;

function animationReducer(
	_state: AnimationState,
	action: AnimationAction,
): AnimationState {
	switch (action.type) {
		case "ENTER":
			return { isVisible: true, isLeaving: false };
		case "LEAVE":
			return { isVisible: true, isLeaving: true };
	}
}

const Toast = ({
	toast,
	onDismiss,
	showCloseButton = true,
	className,
	...props
}: ToastProps) => {
	const [{ isVisible, isLeaving }, dispatch] = useReducer(animationReducer, {
		isVisible: false,
		isLeaving: false,
	});

	useEffect(() => {
		// Show animation
		const enterTimer = setTimeout(() => dispatch({ type: "ENTER" }), 10);

		// Start exit animation before auto-dismiss
		const exitTimer = setTimeout(
			() => dispatch({ type: "LEAVE" }),
			(toast.duration || 3000) - 300,
		);

		return () => {
			clearTimeout(enterTimer);
			clearTimeout(exitTimer);
		};
	}, [toast.duration]);

	const handleDismiss = () => {
		dispatch({ type: "LEAVE" });
		setTimeout(() => onDismiss(toast.id), 300);
	};

	const getIcon = () => {
		switch (toast.type) {
			case "success":
				return <CheckCircle size={24} className="text-success" />;
			case "error":
				return <XCircle size={24} className="text-danger" />;
			case "warning":
				return <AlertTriangle size={24} className="text-warning" />;
			default:
				return <Info size={24} className="text-info" />;
		}
	};

	const getBackgroundColor = () => {
		switch (toast.type) {
			case "success":
				return "bg-success-surface border-border-success-light";
			case "error":
				return "bg-danger-surface border-border-danger-light";
			case "warning":
				return "bg-warning-surface border-border-warning-light";
			default:
				return "bg-info-surface border-border-info-light";
		}
	};

	const getTextColor = () => {
		switch (toast.type) {
			case "success":
				return "text-success-dark";
			case "error":
				return "text-danger-dark";
			case "warning":
				return "text-warning-dark";
			default:
				return "text-info-dark";
		}
	};

	return (
		<div
			className={cn(
				"flex items-center justify-between p-4 mb-3 rounded-lg border shadow-lg transition-all duration-300 ease-in-out min-w-64 max-w-96",
				getBackgroundColor(),
				isVisible && !isLeaving
					? "transform translate-x-0 opacity-100"
					: "transform translate-x-full opacity-0",
				className,
			)}
			{...props}
		>
			<div
				className={cn("flex items-center flex-1", showCloseButton && "pr-3")}
			>
				<div className="flex-shrink-0">{getIcon()}</div>
				<div className={cn("ml-3 flex-1", getTextColor())}>
					{toast.title && (
						<div className="text-sm font-semibold mb-1">{toast.title}</div>
					)}
					<div
						className={cn(
							"text-sm",
							toast.title ? "font-normal" : "font-medium",
						)}
					>
						{toast.message}
					</div>
				</div>
			</div>
			{showCloseButton && (
				<button
					onClick={handleDismiss}
					className={cn(
						"flex-shrink-0 rounded-lg p-1.5 hover:bg-black/10 focus:outline-none focus:ring-2 focus:ring-border-primary transition-colors",
						getTextColor(),
					)}
				>
					<X size={16} />
				</button>
			)}
		</div>
	);
};

interface ToastContainerProps extends React.HTMLAttributes<HTMLDivElement> {
	toasts: Toast[];
	onDismiss: (id: string) => void;
	showCloseButton?: boolean;
}

export const ToastContainer = ({
	toasts,
	onDismiss,
	showCloseButton = true,
	className,
	...props
}: ToastContainerProps) => {
	if (toasts.length === 0) return null;

	return (
		<div
			className={cn(
				"fixed top-4 left-1/2 transform -translate-x-1/2 z-popover space-y-2 flex flex-col items-center",
				className,
			)}
			{...props}
		>
			{toasts.map((toast) => (
				<Toast
					key={toast.id}
					toast={toast}
					onDismiss={onDismiss}
					showCloseButton={showCloseButton}
				/>
			))}
		</div>
	);
};

export default Toast;
